import { useState } from "react";
import { createRoute, redirect } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { Route as RootRoute } from "./__root";
import { readAuthSnapshot } from "../lib/authStore";
import { changePasswordSchema, type ChangePasswordInput } from "../schemas/login";
import { apiCall, ApiError } from "../lib/apiClient";
import { InlineAlert } from "@/components/application/alerts/inline-alert";
import { Button } from "@/components/base/buttons/button";
import { Input } from "@/components/base/input/input";

/**
 * `/profile/change-password` — autentizovaný self-service change password (UC-011).
 *
 * Guard: musí být přihlášený (libovolná role). Pokud anonymous → /login.
 */
export const Route = createRoute({
  getParentRoute: () => RootRoute,
  path: "/profile/change-password",
  beforeLoad: ({ location }) => {
    const auth = readAuthSnapshot();
    if (auth.state === "anonymous") {
      throw redirect({
        to: "/login",
        search: { next: encodeURIComponent(location.href) },
      });
    }
  },
  component: ChangePasswordComponent,
});

function ChangePasswordComponent() {
  const [success, setSuccess] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const form = useForm<ChangePasswordInput>({
    resolver: zodResolver(changePasswordSchema),
    defaultValues: { currentPassword: "", newPassword: "", confirmPassword: "" },
  });
  const mutation = useMutation({
    mutationFn: (input: ChangePasswordInput) =>
      apiCall<void>("/api/profile/change-password", {
        method: "POST",
        body: JSON.stringify({
          current_password: input.currentPassword,
          new_password: input.newPassword,
        }),
      }),
    onSuccess: () => {
      setServerError(null);
      setSuccess(true);
      form.reset();
    },
    onError: (err: unknown) => {
      if (err instanceof ApiError) {
        if (err.status === 401 || err.errorCode === "INVALID_CURRENT_PASSWORD") {
          setServerError("Aktuální heslo není správné.");
          return;
        }
        setServerError(err.message || "Změna hesla se nezdařila.");
        return;
      }
      setServerError("Neznámá chyba sítě.");
    },
  });

  const validateOnChange = form.formState.isSubmitted;

  return (
    <main className="min-h-screen bg-primary px-4 py-12 sm:bg-secondary md:px-8 md:pt-24">
      <div className="flex w-full flex-col gap-6 bg-primary sm:mx-auto sm:max-w-110 sm:rounded-2xl sm:px-10 sm:py-8 sm:shadow-sm">
        <div className="flex flex-col gap-2">
          <h1 className="text-xl font-semibold text-primary md:text-display-xs">Změnit heslo</h1>
          <p className="text-md text-tertiary">Z bezpečnostních důvodů zadejte aktuální heslo.</p>
        </div>
        {success && (
          <InlineAlert variant="success" title="Heslo bylo změněno">
            Pro další session použijte nové heslo.
          </InlineAlert>
        )}
        {serverError && <InlineAlert variant="error">{serverError}</InlineAlert>}
        <form
          noValidate
          onSubmit={form.handleSubmit((v) => mutation.mutate(v))}
          className="flex flex-col gap-6"
        >
          <Input
            label="Aktuální heslo"
            type="password"
            autoComplete="current-password"
            size="lg"
            isRequired
            isInvalid={Boolean(form.formState.errors.currentPassword)}
            hint={form.formState.errors.currentPassword?.message ?? undefined}
            value={form.watch("currentPassword")}
            onChange={(v) => form.setValue("currentPassword", v, { shouldValidate: validateOnChange })}
          />
          <Input
            label="Nové heslo"
            type="password"
            autoComplete="new-password"
            size="lg"
            isRequired
            isInvalid={Boolean(form.formState.errors.newPassword)}
            hint={form.formState.errors.newPassword?.message ?? "Minimálně 14 znaků."}
            value={form.watch("newPassword")}
            onChange={(v) => form.setValue("newPassword", v, { shouldValidate: validateOnChange })}
          />
          <Input
            label="Nové heslo znovu"
            type="password"
            autoComplete="new-password"
            size="lg"
            isRequired
            isInvalid={Boolean(form.formState.errors.confirmPassword)}
            hint={form.formState.errors.confirmPassword?.message ?? undefined}
            value={form.watch("confirmPassword")}
            onChange={(v) => form.setValue("confirmPassword", v, { shouldValidate: validateOnChange })}
          />
          <Button
            type="submit"
            color="primary"
            size="lg"
            isLoading={mutation.isPending}
            isDisabled={mutation.isPending}
          >
            Změnit heslo
          </Button>
        </form>
      </div>
    </main>
  );
}
